/**
 * Resolves the name a connection's own parameter set gives its `oauthConnection`
 * parameter.
 *
 * The key belongs to the parameter set, not the connector: a multi-auth connector can
 * offer an OAuth-connection set alongside a key-auth one, and only the set the record
 * actually selected decides whether there is a key at all. Lookups therefore take both
 * the connector id and the set name.
 *
 * Paths that walk `connections.json` see many connectors at once and so cannot use
 * `useServiceProviderConnector`, which resolves one. This batches the same query —
 * same key, same cache — so the name is read from the manifest rather than assumed.
 */
import { useQueries, type QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import {
  fetchServiceProviderConnector,
  type ServiceProviderConnectorResponse,
} from '@/services/logicAppsApi';
import {
  buildConnectorManifest,
  getConnectionParameterSets,
} from '@/services/manifestConnectionService';
import {
  extractOperationGroup,
  getOAuthConnectionParameterKey,
  isOAuthConnectionParameterSet,
  isServiceProviderConnector,
  selectConnectionParameterSet,
} from '@/utils/connections';
import { serviceProviderConnectorQueryKey } from './useServiceProviderConnector';

const STALE_TIME = 1000 * 60 * 60 * 8;

const QUERY_OPTIONS = {
  staleTime: STALE_TIME,
  gcTime: STALE_TIME,
  refetchOnWindowFocus: false,
  refetchOnReconnect: true as const,
  retry: 2,
};

/** The name the record's own parameter set gives its `oauthConnection` parameter. */
function readOAuthParameterKey(
  connectorId: string,
  connector: ServiceProviderConnectorResponse | null,
  parameterSetName: string | undefined,
): string | undefined {
  const manifest = buildConnectorManifest({
    connectorId,
    serviceProviderConnector: connector,
    operationManifest: null,
  });
  const parameterSet = selectConnectionParameterSet(
    getConnectionParameterSets(manifest),
    parameterSetName,
  );

  return parameterSet && isOAuthConnectionParameterSet(parameterSet)
    ? getOAuthConnectionParameterKey(parameterSet)
    : undefined;
}

export interface OAuthConnectionParameterKeys {
  /** `undefined` when the record's set carries its own credential, or is not resolved yet. */
  getParameterKey: (
    connectorId: string | undefined,
    parameterSetName: string | undefined,
  ) => string | undefined;
  /** Lower-cased ids still resolving, for which a missing key means "not known yet". */
  pending: Set<string>;
  /** Lower-cased ids whose fetch failed, where a missing key is also "not known". */
  errored: Set<string>;
  refetch: () => void;
}

// Only a service-provider connector can declare an `oauthConnection` parameter, so every
// other reference is skipped rather than fetched.
function resolveOperationGroup(connectorId: string): string | null {
  if (!connectorId || !isServiceProviderConnector(connectorId)) return null;
  return extractOperationGroup(connectorId) || null;
}

export function useOAuthConnectionParameterKeys(
  connectorIds: Array<string | undefined>,
): OAuthConnectionParameterKeys {
  const { getOttoToken } = useAuth();
  const { selectedApp, selectedProject } = useAppStore();

  const uniqueConnectorIds = useMemo(() => {
    const seen = new Map<string, string>();
    for (const id of connectorIds) {
      if (!id || !resolveOperationGroup(id)) continue;
      const key = id.toLowerCase();
      if (!seen.has(key)) seen.set(key, id);
    }
    return [...seen.values()];
  }, [connectorIds]);

  const results = useQueries({
    queries: uniqueConnectorIds.map((connectorId) => {
      const operationGroup = resolveOperationGroup(connectorId);
      return {
        queryKey: serviceProviderConnectorQueryKey(operationGroup),
        queryFn: async (): Promise<ServiceProviderConnectorResponse | null> => {
          if (!selectedApp || !selectedProject || !operationGroup) return null;
          return fetchServiceProviderConnector({
            projectName: selectedProject,
            appName: selectedApp.name,
            getToken: getOttoToken,
            operationGroup,
          });
        },
        enabled: !!selectedApp && !!selectedProject && !!operationGroup,
        ...QUERY_OPTIONS,
      };
    }),
  });

  const resolvedConnectors = results.map((result) => result.data ?? null);
  // Query results re-identify every render; their last-updated stamps do not.
  const resolvedSignature = results.map((result) => result.dataUpdatedAt).join('|');
  // A disabled query stays `pending` forever, so only an in-flight first fetch counts.
  const pendingSignature = results.map((result) => (result.isLoading ? '1' : '0')).join('');
  const erroredSignature = results.map((result) => (result.isError ? '1' : '0')).join('');

  // Tracked by ref so `refetch` keeps a stable identity while still targeting the
  // current observers — the signature alone can repeat across different connector sets.
  const resultsRef = useRef(results);
  useEffect(() => {
    resultsRef.current = results;
  });

  const refetch = useCallback(() => {
    resultsRef.current.forEach((result) => void result.refetch());
  }, []);

  const keys = useMemo(() => {
    const resolved = new Map<string, ServiceProviderConnectorResponse | null>();

    uniqueConnectorIds.forEach((connectorId, index) => {
      resolved.set(connectorId.toLowerCase(), resolvedConnectors[index] ?? null);
    });

    return resolved;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueConnectorIds, resolvedSignature]);

  // Per connector rather than a single flag: one slow connector must not make every other
  // service-provider reference look unresolved.
  const pending = useMemo(() => {
    const stillResolving = new Set<string>();

    uniqueConnectorIds.forEach((connectorId, index) => {
      if (pendingSignature[index] === '1') stillResolving.add(connectorId.toLowerCase());
    });

    return stillResolving;
  }, [uniqueConnectorIds, pendingSignature]);

  const errored = useMemo(() => {
    const failed = new Set<string>();

    uniqueConnectorIds.forEach((connectorId, index) => {
      if (erroredSignature[index] === '1') failed.add(connectorId.toLowerCase());
    });

    return failed;
  }, [uniqueConnectorIds, erroredSignature]);

  const getParameterKey = useCallback(
    (connectorId: string | undefined, parameterSetName: string | undefined) => {
      if (!connectorId) return undefined;
      const lookup = connectorId.toLowerCase();
      if (!keys.has(lookup)) return undefined;
      return readOAuthParameterKey(connectorId, keys.get(lookup) ?? null, parameterSetName);
    },
    [keys],
  );

  // Callers put this in dependency arrays, so a fresh object every render would defeat
  // their memoization.
  return useMemo(
    () => ({ getParameterKey, pending, errored, refetch }),
    [getParameterKey, pending, errored, refetch],
  );
}

/**
 * On-demand equivalent for async callers that must not act on a half-loaded cache.
 * Shares the hook's query key, so a warm cache costs nothing and a cold one fetches
 * once. Rejects when the connector cannot be resolved, letting the caller surface a
 * real failure instead of silently treating the connection as file-only.
 */
export async function fetchOAuthConnectionParameterKey(
  queryClient: QueryClient,
  params: {
    connectorId: string;
    parameterSetName: string | undefined;
    projectName: string;
    appName: string;
    getToken: () => Promise<string | null>;
  },
): Promise<string | undefined> {
  const { connectorId, parameterSetName, projectName, appName, getToken } = params;
  const operationGroup = resolveOperationGroup(connectorId);
  if (!operationGroup) return undefined;

  const connector = await queryClient.fetchQuery({
    queryKey: serviceProviderConnectorQueryKey(operationGroup),
    queryFn: (): Promise<ServiceProviderConnectorResponse | null> =>
      fetchServiceProviderConnector({ projectName, appName, getToken, operationGroup }),
    ...QUERY_OPTIONS,
  });

  return readOAuthParameterKey(connectorId, connector ?? null, parameterSetName);
}
